import axios from './_axios_config';
import router from '@/routes';
import swal from 'sweetalert2';
import { hideLoading } from '@/utils/loadingHelper';

const customPath = (resource, ...params) => {
    const queryString = params.filter(param => param).join('&');
    return `/users/clients/${resource}${queryString ? `?${queryString}` : ''}`;
}

const processRequest = async (req) => {
    try {
        const { data, status } = await req

        if (status >= 200 && status < 300) {
            return {
                status: true,
                ...data
            }
        } else {
            throw {
                status: false,
                ...data
            }
        }

    } catch (error) {

        if (error.intercepted) {
            return {
                error: error.details,
                status: false,
                handled: true
            };
        }

        const details = error.response?.data?.details;
        const message = error.response?.data?.message;
        const code = error.response?.data?.code;

        // erros que o servidor identifica por code: casar pelo code é mais
        // firme do que pelo texto de details, que muda sem aviso
        const byCode = {
            PLAN_REQUIRED: "Escolha um plano para continuar.",
            PLAN_NOT_FOUND: "O plano selecionado não está mais disponível. Recarregue a página e tente de novo.",
            PLANS_NOT_CONFIGURED: "Os planos não estão configurados no servidor. Entre em contato com o suporte.",
        };

        if (code === 'WAREHOUSE_LIMIT_REACHED') {
            const limit = Number(error.response?.data?.limit) || 0;
            return {
                error: `Seu plano inclui ${limit} ${limit === 1 ? 'galpão' : 'galpões'}. Assine para adicionar mais.`,
                status: false,
                handled: true
            };
        }

        if (code && byCode[code]) {
            return {
                error: byCode[code],
                status: false,
                handled: true
            };
        }

        if (details) {
            if (details.includes("daily suggestion limit reached")) {
                return {
                    error: "Você atingiu o limite de sugestões de hoje. Tente novamente amanhã.",
                    status: false,
                    handled: true
                };
            }

            // planilha do SINIR ainda não baixada: não é falha do usuário
            if (details.includes("LIR spreadsheet not downloaded yet")) {
                return {
                    error: "Os projetos LIR ainda não estão disponíveis no momento",
                    status: false,
                    unavailable: true,
                    handled: true
                };
            }

            if (details.includes("No subscription found. Subscribe to a plan to continue.")) {
                hideLoading();

                await new swal({
                    title: 'Assinatura necessária',
                    text: 'Nenhuma assinatura encontrada. Assine nosso plano para continuar.',
                    icon: 'warning',
                    customClass: { confirmButton: "btn btn-primary" },
                    buttonsStyling: false,
                });

                router.push({ name: 'perfil', query: { tab: 'billing' } });

                return {
                    error: "Nenhuma assinatura encontrada. Assine nosso plano para continuar.",
                    status: false,
                    handled: true,
                    isEmpty: true,
                    redirected: true
                }
            }

            if (details.includes("parent product uses weight — derived product must also send weight")) {
                return {
                    error: "O produto principal que está derivando não possui esse metodo de medida.",
                    status: false,
                    handled: true,
                    isEmpty: true
                }
            }

            if (details.includes("driver has no active checkin to checkout from")) {
                return {
                    error: "O motorista precisa fazer o Check in antes de registrar o Check out.",
                    status: false,
                    handled: true,
                    isEmpty: true
                }
            }

            if (details.includes("must be a positive number")) {
                return {
                    error: "O valor de venda não pode ser zero ou um número negativo.",
                    status: false,
                    handled: true,
                    isEmpty: true
                }
            }

            if (details.includes("this invoice has no NFe associated")) {
                return {
                    error: "Não foi gerado Nota Fiscal para este produto. Você precisa ativar nas configurações.",
                    status: false,
                    handled: true,
                    isEmpty: true
                }
            }

            if (details.includes("street_type is required in the warehouse address before registering the certificate")) {
                return {
                    error: "Necessário informar o tipo do logradouro no endereço do galpão",
                    status: false,
                    handled: true,
                    isEmpty: true
                }
            }

            if (details.includes("It is necessary to register the warehouse address before registering the certificate.")) {
                return {
                    error: "Necessário cadastrar o endereço do galpão antes de subir o certificado.",
                    status: false,
                    handled: true,
                    isEmpty: true
                }
            }

            if (details.includes("product with this name already exists")) {
                return {
                    error: "Um produto com esse nome ja foi cadastrado nesse galpão.",
                    status: false,
                    handled: true,
                    isEmpty: true
                }
            }

            if (details.includes("prefecture account already requested")) {
                return {
                    error: "O cadastro de sua prefeitura ja foi solicitado anteriormente.",
                    status: false,
                    handled: true,
                    isEmpty: true
                }
            }

            if (details.includes("prefecture not active or aproved by admin")) {
                return {
                    error: "Sua conta de prefeitura ainda nao foi aprovada pelos administradores.",
                    status: false,
                    handled: true,
                    isEmpty: true
                }
            }

            if (details.includes("You do not have a prefecture registered")) {
                return {
                    error: "Você precisa cadastrar uma prefeitura para continuar.",
                    status: false,
                    handled: true,
                    isEmpty: true
                }
            }

            if (details.includes("address not yet registered")) {
                return {
                    error: "Você precisa cadastrar um endereço para continuar.",
                    status: false,
                    handled: true,
                    isEmpty: true
                }
            }

            if (details.includes("weights_imgs") && details.includes("required fields missing")) {
                return {
                    error: "Você precisa fornecer pelo menos uma foto da pesagem do material.",
                    status: false,
                    isEmpty: true,
                    handled: true
                };
            }

            if (details.includes("no suppliers")) {
                return {
                    error: "Nenhum fornecedor cadastrado. Cadastre um fornecedor para continuar.",
                    status: false,
                    isEmpty: true,
                    handled: true
                };
            }

            if (details.includes("no customers")) {
                return {
                    error: "Nenhum cliente cadastrado. Cadastre um cliente para continuar.",
                    status: false,
                    isEmpty: true,
                    handled: true
                };
            }

            if (details.includes("no collaborators")) {
                return {
                    error: "Nenhum colaborador cadastrado.",
                    status: false,
                    isEmpty: true,
                    handled: true
                };
            }

            if (details.includes("only admins can perform this action")) {
                hideLoading();

                await new swal({
                    title: 'Acesso Negado',
                    text: 'Apenas administradores podem realizar esta ação',
                    icon: 'error',
                    customClass: {
                        confirmButton: "btn btn-primary",
                    },
                    buttonsStyling: false,
                });

                router.push('/');

                return {
                    error: "Apenas administradores podem realizar esta ação",
                    status: false,
                    handled: true,
                    redirected: true
                };
            }

            if (details.includes("schedule") && details.includes("required")) {
                return {
                    error: "O campo de horários é obrigatório",
                    status: false,
                    handled: true
                };
            }

            if (details.includes("Invalid day_of_week")) {
                return {
                    error: "Dia da semana inválido. Use valores de 0 (Domingo) a 6 (Sábado)",
                    status: false,
                    handled: true
                };
            }

            if (details.includes("Duplicate day_of_week")) {
                return {
                    error: "Dia da semana duplicado na escala",
                    status: false,
                    handled: true
                };
            }

            if (details.includes("start_time") && details.includes("end_time")) {
                return {
                    error: "O horário de início deve ser anterior ao horário de término",
                    status: false,
                    handled: true
                };
            }

            if (details.includes("cannot set schedule for an admin")) {
                return {
                    error: "Não é possível atribuir escala para uma conta de administrador",
                    status: false,
                    handled: true
                };
            }

            if (details.includes("not found in tenant") || details.includes("collaborator") && details.includes("not found")) {
                return {
                    error: "Colaborador não encontrado",
                    status: false,
                    handled: true
                };
            }

            if (details.includes("first_name") && details.includes("is required")) {
                return {
                    error: "Campos obrigatórios ausentes. Verifique nome, sobrenome, email, senha ou celular",
                    status: false,
                    handled: true
                };
            }

            if (details.includes("check your email")) {
                return {
                    status: true,
                    message: "check your email",
                    handled: true
                };
            }

            if (details.includes("note") && details.includes("required")) {
                return {
                    error: "É necessário informar uma justificativa ao alterar o estoque",
                    status: false,
                    handled: true
                };
            }

            if (details.includes("No fields to update") || details.includes("no fields")) {
                return {
                    error: "Nenhum campo foi enviado para atualização",
                    status: false,
                    handled: true
                };
            }

            if (details.includes("Invalid file type") || details.includes("invalid file")) {
                return {
                    error: "Tipo de arquivo inválido",
                    status: false,
                    handled: true
                };
            }

            if (details.includes("insufficient quantity in stock")) {
                const productIdMatch = details.match(/product_id\s+(\d+)/i);
                const availableMatch = details.match(/Available:\s*([\d.]+)/i);
                const requestedMatch = details.match(/Requested:\s*([\d.]+)/i);
                const productId = productIdMatch ? `#${productIdMatch[1]}` : '';
                const available = availableMatch ? availableMatch[1] : '?';
                const requested = requestedMatch ? requestedMatch[1] : '?';
                return {
                    error: `Produto ${productId}: quantidade insuficiente em estoque. Disponível: ${available} unidades, Solicitado: ${requested} unidades`,
                    status: false,
                    handled: true
                };
            }

            if (details.includes("insufficient weight in stock")) {
                const productIdMatch = details.match(/product_id\s+(\d+)/i);
                const availableMatch = details.match(/Available:\s*([\d.]+)/i);
                const requestedMatch = details.match(/Requested:\s*([\d.]+)/i);
                const productId = productIdMatch ? `#${productIdMatch[1]}` : '';
                const available = availableMatch ? availableMatch[1] : '?';
                const requested = requestedMatch ? requestedMatch[1] : '?';
                return {
                    error: `Produto ${productId}: peso insuficiente em estoque. Disponível: ${available} kg, Solicitado: ${requested} kg`,
                    status: false,
                    handled: true
                };
            }

            if (details.includes("not found in this warehouse")) {
                const productIdMatch = details.match(/product_id\s+(\d+)/i);
                const productId = productIdMatch ? `#${productIdMatch[1]}` : '';
                return {
                    error: `Produto ${productId} não encontrado neste galpão`,
                    status: false,
                    handled: true
                };
            }

            if (details.includes("Insufficient product quantity in stock")) {
                const availableMatch = details.match(/Available:\s*(\d+)/);
                const requestedMatch = details.match(/Requested:\s*(\d+)/);

                const available = availableMatch ? availableMatch[1] : 'desconhecida';
                const requested = requestedMatch ? requestedMatch[1] : 'desconhecida';

                return {
                    error: `Quantidade insuficiente em estoque. Disponível: ${available} unidades, Solicitado: ${requested} unidades`,
                    status: false,
                    handled: true
                };
            }

            if (details.includes("Insufficient product weight in stock")) {
                const availableMatch = details.match(/Available:\s*(\d+)/);
                const requestedMatch = details.match(/Requested:\s*(\d+)/);

                const available = availableMatch ? availableMatch[1] : 'desconhecida';
                const requested = requestedMatch ? requestedMatch[1] : 'desconhecida';

                return {
                    error: `Quantidade insuficiente em estoque. Disponível: ${available} quilogramas, Solicitado: ${requested} quilogramas`,
                    status: false,
                    handled: true
                };
            }
        }

        if (message) {
            if (message.includes("Payment Required. An active subscription is required to access this resource.")) {
                hideLoading();

                await new swal({
                    title: 'Pagamento pendente',
                    text: 'Pagamento não efetuado. Regularize sua assinatura para prosseguir.',
                    icon: 'warning',
                    customClass: { confirmButton: "btn btn-primary" },
                    buttonsStyling: false,
                });

                router.push({ name: 'perfil', query: { tab: 'billing' } });

                return {
                    error: "Pagamento não efetuado. Regularize para prosseguir!",
                    status: false,
                    handled: true,
                    redirected: true,
                    original: error
                };
            }

            if (message.includes("invalid code")) {
                return {
                    error: "Código de verificação incorreto",
                    status: false,
                    handled: true,
                    original: error
                };
            }

            if (message.includes("The certificate password is invalid")) {
                return {
                    error: "A senha do certificado está incorreta.",
                    status: false,
                    handled: true,
                    isEmpty: true
                }
            }

            if (message.includes("User registered in the system")) {
                return {
                    error: "Usuário já cadastrado no sistema",
                    status: false,
                    handled: true
                };
            }

            if (message.includes("Insufficient stock")) {
                return {
                    error: "Estoque insuficiente para realizar esta venda",
                    status: false,
                    handled: true
                };
            }

            if (message.includes("Product type mismatch")) {
                return {
                    error: "Tipo de produto incompatível com a operação",
                    status: false,
                    handled: true
                };
            }

            if (message.includes("Weight and quantity are mutually exclusive")) {
                return {
                    error: "Envie apenas peso OU quantidade, não ambos",
                    status: false,
                    handled: true
                };
            }

            if (message.includes("Error generating contract") || message.includes("Error saving signature")) {
                return {
                    error: "Erro ao processar o contrato. Tente novamente",
                    status: false,
                    handled: true
                };
            }
        }

        const plugnotasFields = error.response?.data?.fields;
        if (plugnotasFields) {
            const fieldErrors = Object.entries(plugnotasFields)
                .map(([field, msg]) => `${field}: ${msg}`)
                .join('\n');
            return {
                error: `Erro de validação PlugNotas:\n${fieldErrors}`,
                status: false,
                handled: true
            };
        }

        if (error.response?.status) {
            const status = error.response.status;

            switch (status) {
                case 409:
                    return {
                        error: "Aparentemente este cadastro já existe no sistema",
                        status: false,
                        handled: true
                    }
                case 403:
                    return {
                        error: "Acesso negado. Verifique se o galpão possui informações jurídicas e certificado digital cadastrados.",
                        status: false,
                        handled: true
                    }
                case 404:
                    if (!details) {
                        return {
                            error: "Nenhum dado foi encontrado",
                            status: false,
                            handled: true
                        }
                    }
                    break;
                case 401:
                    return {
                        error: "A sessão expirou, por favor, faça login novamente",
                        status: false,
                        handled: true
                    }
                case 400:
                    return {
                        error: error.response.data?.message || "Dados inválidos. Verifique os campos",
                        status: false,
                        handled: true
                    }
                case 402:
                    return {
                        error: details || "Pagamento recusado pela operadora. Verifique os dados do cartão ou utilize outro cartão.",
                        status: false,
                        handled: true,
                        declined: true
                    }
                case 500:
                    return {
                        error: "Erro no servidor. Tente novamente mais tarde",
                        status: false,
                        handled: true
                    }
                default:
                    return {
                        error: "Poxa, algo deu errado. Tente novamente, se o problema persistir, contate o suporte",
                        status: false,
                        handled: true
                    };
            }
        }

        if (error.status) {
            switch (error.status) {
                case 409:
                    return {
                        error: "Aparentemente este cadastro já existe no sistema",
                        status: false,
                        handled: true
                    }
                case 404:
                    return {
                        error: "Nenhum dado foi encontrado",
                        status: false,
                        handled: true
                    }
                case 401:
                    return {
                        error: "A sessão expirou, por favor, faça login novamente",
                        status: false,
                        handled: true
                    }
                default:
                    return {
                        error: "Poxa, algo deu errado. Tente novamente, se o problema persistir, contate o suporte",
                        status: false,
                        handled: true
                    };
            }
        }
    }

    return {
        error: "Algo deu errado. Tente novamente",
        status: false,
        handled: true
    }
}

export const ct_clients = {
    async setNewWarehouse(token, payload) {
        try {
            const success = await processRequest(
                axios.post(customPath('warehouse'), {
                    ...payload
                }, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                if (success.redirected) {
                    return success;
                }
                throw success.error
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async getWarehouses(token, page = 1, limit = 10) {
        try {
            const success = await processRequest(
                axios.get(customPath('warehouses', page, limit), {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                if (success.redirected) {
                    return success;
                }
                throw success.error
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async deleteWarehouse(token, id) {
        try {
            const success = await processRequest(
                axios.delete(customPath(`warehouse/${id}`), {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                if (success.redirected) {
                    return success;
                }
                throw success.error
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async getLirProjects(token, { page = 1, limit = 20, status = '', state = '', search = '' } = {}) {
        try {
            const path = customPath('lir/projects',
                `page=${page}`, `limit=${limit}`,
                status ? `status=${encodeURIComponent(status)}` : null,
                state ? `state=${encodeURIComponent(state)}` : null,
                search ? `search=${encodeURIComponent(search)}` : null);

            const success = await processRequest(
                axios.get(path, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                if (success.redirected || success.unavailable) {
                    return success;
                }
                throw success.error
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async getProducts(token, warehouse_id, page = 1, limit = 10, search = '') {
        try {

            if (!warehouse_id) {
                throw {
                    error: "Nenhum id de galpão foi fornecido",
                    status: false
                }
            }

            const path = customPath('products',
                `warehouse_id=${warehouse_id}`,
                `page=${page}`, `limit=${limit}`,
                search ? `search=${encodeURIComponent(search)}` : null);

            const success = await processRequest(
                axios.get(path, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                if (success.redirected) {
                    return success;
                }
                throw success.error
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async createSupplier(token, payload) {
        try {
            const success = await processRequest(
                axios.post(customPath('signup/supplier'), {
                    ...payload
                }, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                if (success.redirected) {
                    return success;
                }
                throw success.error
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async getSuppliers(token, page = 1, limit = 10) {
        try {
            const success = await processRequest(
                axios.get(customPath('suppliers', `page=${page}`, `limit=${limit}`), {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                if (success.redirected) {
                    return success;
                }
                throw success.error
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async setNewPurchase(token, payload) {
        try {
            const formData = new FormData();

            for (const [key, value] of Object.entries(payload)) {
                if (value === null || value === undefined) continue;

                if (key === 'products') {
                    formData.append('products', JSON.stringify(value));
                } else if (Array.isArray(value)) {
                    value.forEach(item => {
                        formData.append(key, item);
                    });
                } else {
                    formData.append(key, value);
                }
            }

            const success = await processRequest(
                axios.post(customPath('purchase'), formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                if (success.redirected) {
                    return success;
                }
                throw success.error
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async setNewSale(token, payload) {
        try {
            const formData = new FormData();

            for (const [key, value] of Object.entries(payload)) {
                if (value === null || value === undefined) continue;

                if (key === 'products') {
                    formData.append('products', JSON.stringify(value));
                } else if (Array.isArray(value)) {
                    value.forEach(item => {
                        formData.append(key, item);
                    });
                } else {
                    formData.append(key, value);
                }
            }

            const success = await processRequest(
                axios.post(customPath('sale'), formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                if (success.redirected) {
                    return success;
                }
                throw success.error
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async createNewProduct(token, payload) {
        try {
            const formData = new FormData();

            for (const [key, value] of Object.entries(payload)) {
                formData.append(key, value);
            }

            const success = await processRequest(
                axios.post(customPath('product'), formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                if (success.redirected) {
                    return success;
                }
                throw success.error
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async updateProduct(token, productId, payload) {
        try {
            const formData = new FormData();

            for (const [key, value] of Object.entries(payload)) {
                if (value === null || value === undefined) continue;
                formData.append(key, value);
            }

            const success = await processRequest(
                axios.put(customPath(`product/${productId}`), formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                if (success.redirected) {
                    return success;
                }
                throw success.error
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async deleteProduct(token, productId) {
        try {
            const success = await processRequest(
                axios.delete(customPath(`product/${productId}`), {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );
            if (!success.status) {
                throw success.error;
            }
            return success;
        } catch (error) {
            return { error, status: false };
        }
    },

    async getUnscreenedLoads(token) {
        try {
            const success = await processRequest(
                axios.get(customPath('unscreened-loads'), { headers: { Authorization: `Bearer ${token}` } })
            );
            if (!success.status) throw success.error;
            return success;
        } catch (error) { return { error, status: false }; }
    },

    async createUnscreenedLoad(token, payload) {
        try {
            const success = await processRequest(
                axios.post(customPath('unscreened-load'), payload, { headers: { Authorization: `Bearer ${token}` } })
            );
            if (!success.status) throw success.error;
            return success;
        } catch (error) { return { error, status: false }; }
    },

    async updateUnscreenedLoad(token, loadId, payload) {
        try {
            const success = await processRequest(
                axios.put(customPath(`unscreened-load/${loadId}`), payload, { headers: { Authorization: `Bearer ${token}` } })
            );
            if (!success.status) throw success.error;
            return success;
        } catch (error) { return { error, status: false }; }
    },

    async deleteUnscreenedLoad(token, loadId) {
        try {
            const success = await processRequest(
                axios.delete(customPath(`unscreened-load/${loadId}`), { headers: { Authorization: `Bearer ${token}` } })
            );
            if (!success.status) throw success.error;
            return success;
        } catch (error) { return { error, status: false }; }
    },

    async createLoadMovement(token, loadId, action, payload) {
        try {
            const success = await processRequest(
                axios.post(customPath(`unscreened-load/${loadId}/movement/${action}`), payload, { headers: { Authorization: `Bearer ${token}` } })
            );
            if (!success.status) throw success.error;
            return success;
        } catch (error) { return { error, status: false }; }
    },

    async getLoadMovements(token, loadId) {
        try {
            const success = await processRequest(
                axios.get(customPath(`unscreened-load/${loadId}/movements`), { headers: { Authorization: `Bearer ${token}` } })
            );
            if (!success.status) throw success.error;
            return success;
        } catch (error) { return { error, status: false }; }
    },

    // triagem: tira material da carga e joga num produto (novo ou existente)
    async screenUnscreenedLoad(token, loadId, payload) {
        try {
            const success = await processRequest(
                axios.post(customPath(`unscreened-load/${loadId}/screening`), payload, { headers: { Authorization: `Bearer ${token}` } })
            );
            if (!success.status) throw success.error;
            return success;
        } catch (error) { return { error, status: false }; }
    },

    // descarte: baixa material da carga sem virar produto (rejeito)
    async discardUnscreenedLoad(token, loadId, payload) {
        try {
            const success = await processRequest(
                axios.post(customPath(`unscreened-load/${loadId}/discard`), payload, { headers: { Authorization: `Bearer ${token}` } })
            );
            if (!success.status) throw success.error;
            return success;
        } catch (error) { return { error, status: false }; }
    },

    async deriveProduct(token, productId, payload) {
        try {
            const formData = new FormData();
            for (const [key, value] of Object.entries(payload)) {
                if (value === null || value === undefined) continue;
                formData.append(key, value);
            }
            const success = await processRequest(
                axios.post(customPath(`product/${productId}/derive`), formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        Authorization: `Bearer ${token}`
                    }
                })
            );
            if (!success.status) {
                throw success.error;
            }
            return success;
        } catch (error) {
            return { error, status: false };
        }
    },

    async createCustomer(token, payload) {
        try {
            const success = await processRequest(
                axios.post(customPath('signup/customer'), {
                    ...payload
                }, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                if (success.redirected) {
                    return success;
                }
                throw success.error
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async getCustomers(token, page = 1, limit = 10) {
        try {
            const success = await processRequest(
                axios.get(customPath('customers', `page=${page}`, `limit=${limit}`), {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                if (success.redirected) {
                    return success;
                }
                throw success.error
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async createCollaborator(token, payload) {
        try {
            const responseFiltered = await processRequest(
                axios.post(customPath('signup/collaborator'), {
                    ...payload
                }, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!responseFiltered.status) {
                if (responseFiltered.redirected) {
                    return responseFiltered;
                }
                if (responseFiltered?.original?.response?.data?.message?.includes("invalid code")) {
                    throw "Código de verificação incorreto"
                }
                throw responseFiltered.error
            }

            if (responseFiltered.message?.includes("check your email")) {
                responseFiltered.stage = "1/2";
                responseFiltered.message = "Um código de verificação foi enviado para o e-mail do colaborador";
            } else {
                responseFiltered.stage = "2/2";
                responseFiltered.message = "Colaborador cadastrado com sucesso!";
            }

            return responseFiltered;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async getCollaborators(token, page = 1, limit = 10) {
        try {
            const success = await processRequest(
                axios.get(customPath('collaborators', `page=${page}`, `limit=${limit}`), {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                if (success.redirected) {
                    return success;
                }
                throw success.error
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async getTransactions(token, { page = 1, limit = 10, product_id = null, warehouse_id = null, start_date = null, end_date = null, action = null, sort_by = null, sort_dir = null, all = false } = {}) {
        try {
            const params = all ? ['all=true'] : [`page=${page}`, `limit=${limit}`];
            if (product_id) params.push(`product_id=${product_id}`);
            if (warehouse_id) params.push(`warehouse_id=${warehouse_id}`);
            if (start_date) params.push(`start_date=${start_date}`);
            if (end_date) params.push(`end_date=${end_date}`);
            if (action) params.push(`action=${encodeURIComponent(Array.isArray(action) ? action.join(',') : action)}`);
            if (sort_by) params.push(`sort_by=${sort_by}`);
            if (sort_dir) params.push(`sort_dir=${sort_dir}`);

            const success = await processRequest(
                axios.get(customPath('transactions', ...params), {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                if (success.redirected) {
                    return success;
                }
                throw success.error
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async getTransactionsSummary(token, { warehouse_id = null, start_date = null, end_date = null, action = null } = {}) {
        try {
            const params = [];
            if (warehouse_id) params.push(`warehouse_id=${warehouse_id}`);
            if (start_date) params.push(`start_date=${start_date}`);
            if (end_date) params.push(`end_date=${end_date}`);
            if (action) params.push(`action=${encodeURIComponent(Array.isArray(action) ? action.join(',') : action)}`);

            const success = await processRequest(
                axios.get(customPath('transactions/summary', ...params), {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                if (success.redirected) {
                    return success;
                }
                throw success.error
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async requestPrefectureAccount(token, payload) {
        try {
            const responseFiltered = await processRequest(
                axios.post(customPath('request_prefecture'), {
                    ...payload
                }, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!responseFiltered.status) {
                if (responseFiltered?.original?.response?.data?.message?.includes("invalid code")) {
                    throw "Código de verificação incorreto"
                }
                throw responseFiltered.error
            }

            if (responseFiltered.message.includes("check your email")) {
                responseFiltered.stage = "1/2";
                responseFiltered.message = "Um código de verificação foi enviado para o seu e-mail. Confirme para concluir o cadastro";
            } else {
                responseFiltered.stage = "2/2";
                responseFiltered.message = "Uma nova conta de prefeitura foi solicitada com sucesso, por favor aguarde nossa aprovação!";
            }

            return responseFiltered;
        } catch (error) {

            return {
                error,
                status: false
            }
        }
    },

    async uploadPoliceReport(token, payload) {
        try {
            const formData = new FormData();

            Object.entries(payload).forEach(([key, value]) => {
                formData.append(key, value);
            });

            const success = await processRequest(
                axios.post(customPath('prefecture/set_police_report'), formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                })
            );

            if (!success.status) {
                if (success.redirected) {
                    return success;
                }
                throw success.error
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async getPoliceReports(token, page = 1, limit = 10) {
        try {
            const success = await processRequest(
                axios.get(customPath('police_report', `page=${page}`, `limit=${limit}`), {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                if (success.redirected) {
                    return success;
                }
                throw success.error
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async setClockSchedule(token, email, schedule) {
        try {
            const success = await processRequest(
                axios.post(customPath(`clock/schedule/${email}`), { schedule }, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                if (success.redirected) {
                    return success;
                }
                throw success.error
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async getClockSchedule(token, email) {
        try {
            const success = await processRequest(
                axios.get(customPath(`clock/schedule/${email}`), {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                if (success.redirected) {
                    return success;
                }
                throw success.error
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async clockToggle(token) {
        try {
            const success = await processRequest(
                axios.post(customPath('clock'), {}, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                if (success.redirected) {
                    return success;
                }
                throw success.error
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async getClockHistory(token, params = {}) {
        try {
            const queryParams = [];

            if (params.start_date) queryParams.push(`start_date=${params.start_date}`);
            if (params.end_date) queryParams.push(`end_date=${params.end_date}`);
            queryParams.push(`page=${params.page || 1}`);
            queryParams.push(`limit=${params.limit || 20}`);

            const success = await processRequest(
                axios.get(customPath('clock/history', ...queryParams), {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                if (success.redirected) {
                    return success;
                }
                throw success.error
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async getClockReport(token, params = {}) {
        try {
            const queryParams = [
                `start_date=${params.start_date}`,
                `end_date=${params.end_date}`
            ];

            if (params.email) queryParams.push(`email=${params.email}`);
            queryParams.push(`page=${params.page || 1}`);
            queryParams.push(`limit=${params.limit || 20}`);

            const success = await processRequest(
                axios.get(customPath('clock/report', ...queryParams), {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                if (success.redirected) {
                    return success;
                }
                throw success.error
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async uploadWarehouseCertificate(token, warehouse_id, payload) {
        try {
            const formData = new FormData();
            formData.append('certificate', payload.certificate);
            formData.append('pw', payload.pw);

            const success = await processRequest(
                axios.put(customPath(`warehouse/${warehouse_id}/juridical/certificate`), formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                })
            );

            if (!success.status) {
                if (success.redirected) return success;
                throw success.error;
            }

            return success;
        } catch (error) {
            return { error, status: false };
        }
    },

    async registerPlugnotasCompany(token, warehouse_id, payload) {
        try {
            const success = await processRequest(
                axios.post(customPath(`warehouse/${warehouse_id}/plugnotas/company`), payload, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );

            if (!success.status) {
                if (success.redirected) return success;
                throw success.error;
            }

            return success;
        } catch (error) {
            return { error, status: false };
        }
    },

    async emitNFSe(token, warehouse_id, payload) {
        try {
            const success = await processRequest(
                axios.post(customPath(`warehouse/${warehouse_id}/plugnotas/nfse`), payload, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );

            if (!success.status) {
                if (success.redirected) return success;
                throw success.error;
            }

            return success;
        } catch (error) {
            return { error, status: false };
        }
    },

    async fetchNFSe(token, invoice_id) {
        try {
            const success = await processRequest(
                axios.get(customPath(`invoice/${invoice_id}/nfe`), {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );

            if (!success.status) {
                if (success.redirected) return success;
                throw success.error;
            }

            return success;
        } catch (error) {
            return { error, status: false };
        }
    },

    async getWarehouseJuridical(token, warehouse_id) {
        try {
            const success = await processRequest(
                axios.get(customPath(`warehouse/${warehouse_id}/juridical`), {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                if (success.redirected) return success;
                throw success.error;
            }

            return success;
        } catch (error) {
            return { error, status: false };
        }
    },

    async setWarehouseJuridical(token, warehouse_id, payload) {
        try {
            const success = await processRequest(
                axios.put(customPath(`warehouse/${warehouse_id}/juridical`), payload, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                if (success.redirected) return success;
                throw success.error;
            }

            return success;
        } catch (error) {
            return { error, status: false };
        }
    },

    async getInvoicePacks(token, params = {}) {
        try {
            const queryParams = [];
            if (params.page) queryParams.push(`page=${params.page}`);
            if (params.limit) queryParams.push(`limit=${params.limit}`);
            if (params.action) queryParams.push(`action=${params.action}`);
            if (params.warehouse_id) queryParams.push(`warehouse_id=${params.warehouse_id}`);

            const success = await processRequest(
                axios.get(customPath('invoice-packs', ...queryParams), {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );

            if (!success.status) {
                if (success.redirected) return success;
                throw success.error;
            }

            return success;
        } catch (error) {
            return { error, status: false };
        }
    },

    async getDashboard(token) {
        try {
            const success = await processRequest(
                axios.get(customPath('dashboard'), {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                if (success.redirected) {
                    return success;
                }
                throw success.error
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async fetchConfig(token) {
        try {
            const success = await processRequest(
                axios.get(customPath('config'), {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );
            if (!success.status) throw success.error;
            return success;
        } catch (error) {
            return { error, status: false };
        }
    },

    async updateConfig(token, payload) {
        try {
            const success = await processRequest(
                axios.put(customPath('config'), payload, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );
            if (!success.status) throw success.error;
            return success;
        } catch (error) {
            return { error, status: false };
        }
    },

    async getTermsStatus(token) {
        try {
            const success = await processRequest(
                axios.get(customPath('config/terms'), {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );
            if (!success.status) throw success.error;
            return success;
        } catch (error) {
            return { error, status: false };
        }
    },

    async acceptTerms(token) {
        try {
            const success = await processRequest(
                axios.post(customPath('config/terms'), null, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );
            if (!success.status) throw success.error;
            return success;
        } catch (error) {
            return { error, status: false };
        }
    },

    async createCashFlow(token, payload) {
        try {
            const success = await processRequest(
                axios.post(customPath('cash-flow'), payload, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );
            if (!success.status) throw success.error;
            return success;
        } catch (error) {
            return { error, status: false };
        }
    },

    async getCashFlow(token, warehouse_id = null) {
        try {
            const success = await processRequest(
                axios.get(customPath('cash-flow', warehouse_id ? `warehouse_id=${warehouse_id}` : null), {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );
            if (!success.status) throw success.error;
            return success;
        } catch (error) {
            return { error, status: false };
        }
    },

    async getCashFlowHistory(token, { start_date = null, end_date = null, warehouse_id = null } = {}) {
        try {
            const params = [];
            if (start_date) params.push(`start_date=${start_date}`);
            if (end_date) params.push(`end_date=${end_date}`);
            if (warehouse_id) params.push(`warehouse_id=${warehouse_id}`);
            const success = await processRequest(
                axios.get(customPath('cash-flow/history', ...params), {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );
            if (!success.status) throw success.error;
            return success;
        } catch (error) {
            return { error, status: false };
        }
    },

    async getInvoice(token, invoice_id) {
        try {
            const success = await processRequest(
                axios.get(customPath(`invoice/${invoice_id}`), {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );
            if (!success.status) throw success.error;
            return success;
        } catch (error) {
            return { error, status: false };
        }
    },

    async cancelInvoice(token, invoice_id, reason) {
        try {
            const success = await processRequest(
                axios.delete(customPath(`invoice/${invoice_id}`), {
                    headers: { Authorization: `Bearer ${token}` },
                    data: reason ? { reason } : undefined
                })
            );
            if (!success.status) throw success.error;
            return success;
        } catch (error) {
            return { error, status: false };
        }
    },

    async cancelInvoiceNfe(token, invoice_id, reason) {
        try {
            const success = await processRequest(
                axios.delete(customPath(`invoice/${invoice_id}/nfe`), {
                    headers: { Authorization: `Bearer ${token}` },
                    data: reason ? { reason } : undefined
                })
            );
            if (!success.status) throw success.error;
            return success;
        } catch (error) {
            return { error, status: false };
        }
    },

    async getInvoiceNfePdf(token, invoice_id, modeloImpressao) {
        try {
            const query = modeloImpressao ? `modeloImpressao=${modeloImpressao}` : null;
            const response = await axios.get(customPath(`invoice/${invoice_id}/nfe/pdf`, query), {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });

            if (response.status === 200) {
                const url = URL.createObjectURL(response.data);
                return { status: true, url };
            }

            // 202: PlugNotas ainda gerando o PDF de forma assíncrona
            const processingText = await response.data.text();
            const processing = JSON.parse(processingText);
            return { status: false, processing: true, error: processing.details || processing.message };
        } catch (error) {
            let details = 'Não foi possível carregar o PDF da nota';
            try {
                if (error.response?.data instanceof Blob) {
                    const errorText = await error.response.data.text();
                    const parsed = JSON.parse(errorText);
                    details = parsed.details || parsed.message || details;
                }
            } catch (parseError) {
                // mantém a mensagem padrão se o corpo não vier em JSON
            }
            return { status: false, processing: false, error: details };
        }
    },

    async getRecurringExpenses(token, warehouse_id = null) {
        try {
            const query = warehouse_id ? `warehouse_id=${warehouse_id}` : null;
            const success = await processRequest(
                axios.get(customPath('recurring-expenses', query), {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );
            if (!success.status) throw success.error;
            return success;
        } catch (error) {
            return { error, status: false };
        }
    },

    async createRecurringExpense(token, payload) {
        try {
            const success = await processRequest(
                axios.post(customPath('recurring-expenses'), payload, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );
            if (!success.status) throw success.error;
            return success;
        } catch (error) {
            return { error, status: false };
        }
    },

    async updateRecurringExpense(token, expense_id, payload) {
        try {
            const success = await processRequest(
                axios.put(customPath(`recurring-expenses/${expense_id}`), payload, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );
            if (!success.status) throw success.error;
            return success;
        } catch (error) {
            return { error, status: false };
        }
    },

    async deleteRecurringExpense(token, expense_id) {
        try {
            const success = await processRequest(
                axios.delete(customPath(`recurring-expenses/${expense_id}`), {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );
            if (!success.status) throw success.error;
            return success;
        } catch (error) {
            return { error, status: false };
        }
    },

    async updateSupplier(token, email, payload) {
        try {
            const success = await processRequest(
                axios.put(customPath(`supplier/${email}`), payload, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );
            if (!success.status) { if (success.redirected) return success; throw success.error; }
            return success;
        } catch (error) { return { error, status: false }; }
    },

    async deleteSupplier(token, email) {
        try {
            const success = await processRequest(
                axios.delete(customPath(`supplier/${email}`), {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );
            if (!success.status) { if (success.redirected) return success; throw success.error; }
            return success;
        } catch (error) { return { error, status: false }; }
    },

    async updateCustomer(token, email, payload) {
        try {
            const success = await processRequest(
                axios.put(customPath(`customer/${email}`), payload, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );
            if (!success.status) { if (success.redirected) return success; throw success.error; }
            return success;
        } catch (error) { return { error, status: false }; }
    },

    async deleteCustomer(token, email) {
        try {
            const success = await processRequest(
                axios.delete(customPath(`customer/${email}`), {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );
            if (!success.status) { if (success.redirected) return success; throw success.error; }
            return success;
        } catch (error) { return { error, status: false }; }
    },

    async createDriver(token, payload) {
        try {
            const success = await processRequest(
                axios.post(customPath('driver'), payload, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );
            if (!success.status) { if (success.redirected) return success; throw success.error; }
            return success;
        } catch (error) { return { error, status: false }; }
    },

    async getDrivers(token, page = 1, limit = 20) {
        try {
            const success = await processRequest(
                axios.get(customPath('drivers', `page=${page}`, `limit=${limit}`), {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );
            if (!success.status) { if (success.redirected) return success; throw success.error; }
            return success;
        } catch (error) { return { error, status: false }; }
    },

    async updateDriver(token, driver_id, payload) {
        try {
            const success = await processRequest(
                axios.put(customPath(`driver/${driver_id}`), payload, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );
            if (!success.status) { if (success.redirected) return success; throw success.error; }
            return success;
        } catch (error) { return { error, status: false }; }
    },

    async deleteDriver(token, driver_id) {
        try {
            const success = await processRequest(
                axios.delete(customPath(`driver/${driver_id}`), {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );
            if (!success.status) { if (success.redirected) return success; throw success.error; }
            return success;
        } catch (error) { return { error, status: false }; }
    },

    async createCheckpoint(token, driver_id, action, plate = null) {
        try {
            const body = action === 'checkin' && plate ? { plate } : {};
            const success = await processRequest(
                axios.post(customPath(`driver/${driver_id}/checkpoint/${action}`), body, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );
            if (!success.status) { if (success.redirected) return success; throw success.error; }
            return success;
        } catch (error) { return { error, status: false }; }
    },

    async getCheckpoints(token, driver_id, page = 1, limit = 20) {
        try {
            const success = await processRequest(
                axios.get(customPath(`driver/${driver_id}/checkpoints`, `page=${page}`, `limit=${limit}`), {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );
            if (!success.status) { if (success.redirected) return success; throw success.error; }
            return success;
        } catch (error) { return { error, status: false }; }
    },

    async createTruck(token, payload) {
        try {
            const success = await processRequest(
                axios.post(customPath('truck'), payload, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );
            if (!success.status) { if (success.redirected) return success; throw success.error; }
            return success;
        } catch (error) { return { error, status: false }; }
    },

    async getTrucks(token, page = 1, limit = 20) {
        try {
            const success = await processRequest(
                axios.get(customPath('trucks', `page=${page}`, `limit=${limit}`), {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );
            if (!success.status) { if (success.redirected) return success; throw success.error; }
            return success;
        } catch (error) { return { error, status: false }; }
    },

    async getTruckByPlate(token, plate) {
        try {
            const success = await processRequest(
                axios.get(customPath(`truck/${plate}`), {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );
            if (!success.status) { if (success.redirected) return success; throw success.error; }
            return success;
        } catch (error) { return { error, status: false }; }
    },

    async updateTruck(token, plate, payload) {
        try {
            const success = await processRequest(
                axios.put(customPath(`truck/${plate}`), payload, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );
            if (!success.status) { if (success.redirected) return success; throw success.error; }
            return success;
        } catch (error) { return { error, status: false }; }
    },

    async deleteTruck(token, plate) {
        try {
            const success = await processRequest(
                axios.delete(customPath(`truck/${plate}`), {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );
            if (!success.status) { if (success.redirected) return success; throw success.error; }
            return success;
        } catch (error) { return { error, status: false }; }
    },

    // ── Assinatura da plataforma (rotas em /subscription, fora de /users/clients) ──
    async getSubscription(token) {
        return await processRequest(
            axios.get('/subscription', {
                headers: { Authorization: `Bearer ${token}` }
            })
        );
    },

    // catalogo de planos: preco, galpoes inclusos e preco do galpao extra.
    // a tela nunca fixa esses numeros, sempre le daqui
    async getSubscriptionPlans(token) {
        return await processRequest(
            axios.get('/subscription/plans', {
                headers: { Authorization: `Bearer ${token}` }
            })
        );
    },

    async subscribePlan(token, payload) {
        return await processRequest(
            axios.post('/subscription/subscribe', payload, {
                headers: { Authorization: `Bearer ${token}` }
            })
        );
    },

    // PIX não é assinatura recorrente na Pagar.me — é cobrança avulsa, por isso
    // tem rota própria e não passa pelo /subscribe dos outros meios
    async subscribePlanPix(token) {
        return await processRequest(
            axios.post('/subscription/subscribe/pix', null, {
                headers: { Authorization: `Bearer ${token}` }
            })
        );
    },

    // cancela o QR gerado e ainda não pago; não mexe na assinatura nem no acesso.
    // não passa pelo processRequest porque precisa distinguir "já foi pago"
    // (que na prática é sucesso) de falha de verdade, e ele achata o code
    async cancelSubscriptionPix(token) {
        try {
            const { data } = await axios.delete('/subscription/subscribe/pix', {
                headers: { Authorization: `Bearer ${token}` }
            });
            return { status: true, data: data?.data ?? data };
        } catch (error) {
            const payload = error.response?.data ?? {};

            // corrida real: o usuário pode pagar no banco com a tela do QR aberta
            if (payload.code === 'PIX_ALREADY_PAID') {
                return { status: false, alreadyPaid: true };
            }

            if (error.response?.status === 404) {
                return { status: false, notFound: true };
            }

            return {
                status: false,
                error: payload.details || payload.message || 'Não foi possível cancelar o PIX',
            };
        }
    },

    async cancelSubscription(token) {
        return await processRequest(
            axios.delete('/subscription', {
                headers: { Authorization: `Bearer ${token}` }
            })
        );
    },

    async getSubscriptionBoleto(token) {
        return await processRequest(
            axios.get('/subscription/boleto', {
                headers: { Authorization: `Bearer ${token}` }
            })
        );
    },

    async getSubscriptionCards(token) {
        return await processRequest(
            axios.get('/subscription/cards', {
                headers: { Authorization: `Bearer ${token}` }
            })
        );
    },

    async addSubscriptionCard(token, payload) {
        return await processRequest(
            axios.post('/subscription/cards', payload, {
                headers: { Authorization: `Bearer ${token}` }
            })
        );
    },

    async deleteSubscriptionCard(token, card_id) {
        return await processRequest(
            axios.delete(`/subscription/cards/${card_id}`, {
                headers: { Authorization: `Bearer ${token}` }
            })
        );
    },

    async createSuggestion(token, payload) {
        return await processRequest(
            axios.post(customPath('suggestion'), payload, {
                headers: { Authorization: `Bearer ${token}` }
            })
        );
    },

    async getMySuggestions(token, { page = 1, limit = 10 } = {}) {
        return await processRequest(
            axios.get(customPath('suggestions', `page=${page}`, `limit=${limit}`), {
                headers: { Authorization: `Bearer ${token}` }
            })
        );
    },
}